/**
 * Turning whatever a codelist Autocomplete hands back into the ATLAS code.
 *
 * The control accepts free text, so `onChange` receives an option object when
 * the user clicks one and a plain STRING when they type. The string was saved
 * verbatim, which meant picking "Direct trade with/by private consumers" from
 * Nature of Transaction stored that sentence in
 * kopf.angabenGeschaftsvorgangArt instead of "12". ATLAS then answered
 * "Required data field" on a field that visibly had a value, and no amount of
 * retyping helped because every attempt saved another label.
 */

export interface CodelistOption {
  code?: string;
  description?: string;
}

/** Loose comparison: case, punctuation and spacing vary between label and text. */
function norm(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * The value to store for a codelist field.
 *
 * An option object yields its code. A typed string is resolved against the
 * options — first as a code, then as a description, then as the "code -
 * description" form the input displays, then as a leading-code prefix. Text
 * that matches nothing is returned trimmed, so a codelist the app failed to
 * load does not silently discard what the user typed.
 */
export function resolveCodelistValue(
  newValue: unknown,
  options?: CodelistOption[] | null,
): string {
  if (newValue && typeof newValue === 'object') {
    const code = (newValue as CodelistOption).code;
    return String(code ?? '').trim();
  }

  const typed = String(newValue ?? '').trim();
  if (!typed || !Array.isArray(options) || options.length === 0) return typed;

  const byCode = options.find((o) => String(o?.code ?? '').trim() === typed);
  if (byCode) return String(byCode.code).trim();

  const target = norm(typed);
  if (!target) return typed;

  const byDescription = options.find((o) => norm(o?.description) === target);
  if (byDescription) return String(byDescription.code ?? '').trim();

  // The input renders options as "code - description"; a user may paste that.
  const combined = options.find(
    (o) => norm(`${o?.code ?? ''} ${o?.description ?? ''}`) === target,
  );
  if (combined) return String(combined.code ?? '').trim();

  // Truncated label ("lirect trade with/by private consumers") — match on a
  // description that starts with, or is started by, what was typed. Only when
  // exactly one option qualifies, so an ambiguous fragment is never guessed.
  const partial = options.filter((o) => {
    const description = norm(o?.description);
    return (
      description.length > 0 &&
      target.length >= 8 &&
      (description.startsWith(target) || target.startsWith(description))
    );
  });
  if (partial.length === 1) return String(partial[0].code ?? '').trim();

  return typed;
}
